/**
 * Validador de Mensagens
 * 
 * Responsável por:
 * - Validar tamanho de payloads
 * - Validar estrutura de mensagens
 * - Validar presença de campos obrigatórios
 * - Validar requisitos de sessão
 * 
 * @module MessageValidator
 */

import { WebViewMessage } from '../../types/messages';
import { MAX_PAYLOAD_BYTES } from '../../constants';
import { sendToTerminal } from '../../../terminalLogger';
import { LogCategory } from '@/config/logging';
import { isWebViewReady } from '../../transport';

export interface ValidationResult {
  valid: boolean;
  error?: {
    requestId?: string;
    status: string;
    message: string;
    code?: string;
  };
}

/**
 * Validador de mensagens para comunicação com CorelDRAW
 */
export class MessageValidator {
  /**
   * Valida o tamanho de um payload JSON
   * 
   * @param payload Payload JSON stringificado
   * @returns Resultado da validação
   */
  public validatePayloadSize(payload: string): ValidationResult {
    if (payload.length > MAX_PAYLOAD_BYTES) {
      sendToTerminal('❌ [MESSAGE_VALIDATOR] Payload muito grande', {
        size: payload.length,
        maxSize: MAX_PAYLOAD_BYTES,
        exceeded: payload.length - MAX_PAYLOAD_BYTES
      }, LogCategory.ERROR);

      return {
        valid: false,
        error: {
          status: 'erro',
          message: `Payload muito grande: ${payload.length} bytes (máximo: ${MAX_PAYLOAD_BYTES} bytes)`,
          code: 'PAYLOAD_TOO_LARGE'
        }
      };
    }

    return { valid: true };
  }

  /**
   * Valida a estrutura básica de uma mensagem
   * 
   * @param message Mensagem a ser validada
   * @returns Resultado da validação
   */
  public validateMessage(message: Partial<WebViewMessage>): ValidationResult {
    // Validar action
    if (!message.action || typeof message.action !== 'string') {
      sendToTerminal('❌ [MESSAGE_VALIDATOR] Action inválida', {
        hasAction: !!message.action,
        actionType: typeof message.action
      }, LogCategory.ERROR);

      return {
        valid: false,
        error: {
          status: 'erro',
          message: 'Campo "action" é obrigatório e deve ser uma string',
          code: 'INVALID_ACTION'
        }
      };
    }

    // Validar requestId
    if (!message.requestId || typeof message.requestId !== 'string') {
      sendToTerminal('❌ [MESSAGE_VALIDATOR] RequestId inválido', {
        hasRequestId: !!message.requestId,
        requestIdType: typeof message.requestId
      }, LogCategory.ERROR);

      return {
        valid: false,
        error: {
          status: 'erro',
          message: 'Campo "requestId" é obrigatório e deve ser uma string',
          code: 'INVALID_REQUEST_ID'
        }
      };
    }

    // Validar data (opcional, mas se existir deve ser objeto)
    if (message.data !== undefined && (typeof message.data !== 'object' || message.data === null || Array.isArray(message.data))) {
      sendToTerminal('❌ [MESSAGE_VALIDATOR] Data inválida', {
        hasData: message.data !== undefined,
        dataType: typeof message.data,
        isNull: message.data === null,
        isArray: Array.isArray(message.data)
      }, LogCategory.ERROR);

      return {
        valid: false,
        error: {
          requestId: message.requestId,
          status: 'erro',
          message: 'Campo "data" deve ser um objeto',
          code: 'INVALID_DATA'
        }
      };
    }

    return { valid: true };
  }

  /**
   * Valida requisitos de sessão para uma mensagem
   * 
   * @param action Action da mensagem
   * @param hasToken Se o token de sessão está disponível
   * @param isTokenExempt Se a action é isenta de token
   * @returns Resultado da validação
   */
  public validateSessionRequirements(
    action: string,
    hasToken: boolean,
    isTokenExempt: boolean
  ): ValidationResult {
    // Actions isentas não precisam de token
    if (isTokenExempt) {
      return { valid: true };
    }

    // Actions não-isentas precisam de token
    if (!hasToken) {
      sendToTerminal('❌ [MESSAGE_VALIDATOR] Token de sessão obrigatório', {
        action,
        hasToken: false,
        isTokenExempt: false
      }, LogCategory.AUTH);

      return {
        valid: false,
        error: {
          status: 'erro',
          message: `Action "${action}" requer token de sessão, mas ele não está disponível`,
          code: 'SESSION_TOKEN_REQUIRED'
        }
      };
    }

    return { valid: true };
  }

  /**
   * Valida uma mensagem completa antes de envio
   * 
   * @param message Mensagem a ser validada
   * @param hasToken Se o token de sessão está disponível
   * @param isTokenExempt Se a action é isenta de token
   * @returns Resultado da validação completa
   */
  public validateComplete(
    message: Partial<WebViewMessage>,
    hasToken: boolean,
    isTokenExempt: boolean
  ): ValidationResult {
    // 1. Validar estrutura
    const structureValidation = this.validateMessage(message);
    if (!structureValidation.valid) {
      return structureValidation;
    }

    // 2. Validar requisitos de sessão
    const sessionValidation = this.validateSessionRequirements(
      message.action!,
      hasToken,
      isTokenExempt
    );
    if (!sessionValidation.valid) {
      return {
        ...sessionValidation,
        error: {
          ...sessionValidation.error!,
          requestId: message.requestId
        }
      };
    }

    // 3. Validar tamanho (se tiver todos os dados)
    if (message.data) {
      const jsonPayload = JSON.stringify(message);
      const sizeValidation = this.validatePayloadSize(jsonPayload);
      if (!sizeValidation.valid) {
        return {
          ...sizeValidation,
          error: {
            ...sizeValidation.error!,
            requestId: message.requestId
          }
        };
      }
    }

    return { valid: true };
  }

  /**
   * Valida disponibilidade do WebView
   * 
   * @returns Se o WebView está disponível
   */
  public isWebViewAvailable(): boolean {
    return isWebViewReady();
  }
}

